import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { persist, devtools } from 'zustand/middleware'
import {
  BUDGETING_PERIODS,
  NON_MODAL_WINDOW_STATE,
  MODAL_WINDOW_STATE,
  ARCHIVE_STEP,
} from '@/data/constants/constants'

let store = immer((set) => ({
  offset: 1,
  numberOfPeriods: 13,
  periodType: BUDGETING_PERIODS.WEEK,
  count: 0,
  dashboardSelectedVaultIds: null,
  dashboardSplitByVault: false,
  // Set when the Status banner's breach link is clicked — Projected balance
  // chart reads this to highlight/scroll to that period. Not persisted
  // (deliberately left out of partialize below): it's a one-shot UI cue,
  // not dashboard configuration.
  highlightedPeriodOffset: null,
  // Space whose data was just reset: shows the "enter your opening balances"
  // banner until dismissed. Persisted so a reload doesn't lose it.
  spaceResetBannerTeamId: null,
  // When the dashboard feeds were last refetched, so the control row can say
  // "Updated 9:42 am · 6 min ago". Epoch ms, not a Date, so the persisted
  // store stays JSON-safe. Never persisted (see partialize) — a timestamp
  // from a previous session would be a lie.
  dashboardRefreshedAt: null,
  // Which team the dashboard's saved-view/team-defaults bootstrap has already
  // been applied for. Not persisted: the bootstrap is a per-session load
  // behaviour, and persisting it would stop it running after a page reload.
  dashboardBootstrappedTeamId: null,
  modalWindowState: MODAL_WINDOW_STATE.CLOSED,
  nonModalWindowState: NON_MODAL_WINDOW_STATE.CLOSED,
  modalWindowType: null,
  nonModalWindowType: null,
  transactionCategoryId: null,
  addOrEditiTransaction: null,
  modalProps: {},
  nonModalName: null,
  nonModalProps: {},
  archiveStep: ARCHIVE_STEP.FIRST,
  archivedVault: undefined,
  archivedEntity: null,
  selectedMyTodoTransactionRowId: null,
  dockTransactionListView: false,
  cellTransactionsLoaded: false,
  setCellTransactionsLoaded: (value) =>
    set((state) => {
      state.cellTransactionsLoaded = value
    }),
  setDockTransactionListView: (value) =>
    set((state) => {
      state.dockTransactionListView = value
    }),
  setSelectedMyTodoTransactionRowId: (value) =>
    set((state) => {
      state.selectedMyTodoTransactionRowId = value
    }),
  setArchiveStep: (value) =>
    set((state) => {
      state.archiveStep = value
    }),
  setNonModalProps: (value) =>
    set((state) => {
      state.nonModalProps = value
    }),
  setModalProps: (value) =>
    set((state) => {
      state.modalProps = value
    }),
  setNonModalName: (value) =>
    set((state) => {
      state.nonModalName = value
    }),
  setDashboardSelectedVaultIds: (value) =>
    set((state) => {
      state.dashboardSelectedVaultIds = value
    }),
  setDashboardSplitByVault: (value) =>
    set((state) => {
      state.dashboardSplitByVault = value
    }),
  setHighlightedPeriodOffset: (value) =>
    set((state) => {
      state.highlightedPeriodOffset = value
    }),
  setSpaceResetBannerTeamId: (value) =>
    set((state) => {
      state.spaceResetBannerTeamId = value
    }),
  setDashboardRefreshedAt: (value) =>
    set((state) => {
      state.dashboardRefreshedAt = value
    }),
  setDashboardBootstrappedTeamId: (value) =>
    set((state) => {
      state.dashboardBootstrappedTeamId = value
    }),
  setEditedEntity: (value) =>
    set((state) => {
      state.editedEntity = value
    }),
  setArchivedEntity: (value) =>
    set((state) => {
      state.archivedEntity = value
    }),
  setArchivedVault: (value) =>
    set((state) => {
      state.archivedVault = value
    }),
  setNonModalWindowState: (value) =>
    set((state) => {
      state.nonModalWindowState = value
    }),
  toggleNonModalWindowState: () =>
    set((state) => {
      state.nonModalWindowState =
        state.nonModalWindowState === NON_MODAL_WINDOW_STATE.CLOSED
          ? NON_MODAL_WINDOW_STATE.OPENED
          : NON_MODAL_WINDOW_STATE.CLOSED
    }),
  setNonModalWindowType: (value) =>
    set((state) => {
      state.nonModalWindowType = value
    }),
  toggleModalWindowState: () =>
    set((state) => {
      state.modalWindowState =
        state.modalWindowState === MODAL_WINDOW_STATE.CLOSED
          ? MODAL_WINDOW_STATE.OPENED
          : MODAL_WINDOW_STATE.CLOSED
    }),
  toggleArchiveStep: () =>
    set((state) => {
      state.archiveStep =
        state.archiveStep === ARCHIVE_STEP.FIRST
          ? ARCHIVE_STEP.SECOND
          : ARCHIVE_STEP.FIRST
    }),
  setModalWindowState: (value) =>
    set((state) => {
      state.modalWindowState = value
    }),
  setModalWindowType: (value) =>
    set((state) => {
      state.modalWindowType = value
    }),

  setOffset: (value) =>
    set((state) => {
      state.offset = value
    }),
  setNumberOfPeriods: (value) =>
    set((state) => {
      state.numberOfPeriods = value
      state.offset = 1
    }),
  setPeriodType: (value) =>
    set((state) => {
      state.periodType = value
    }),
  reset: () =>
    set({
      archivedEntity: null,
      modalWindowType: null,
      archivedVault: null,
    }),
  // increment: (qty) =>
  //   set((state) => {
  //     state.count += qty
  //   }),
  // decrement: (qty) =>
  //   set((state) => {
  //     state.count -= qty
  //   }),
}))

store = devtools(store)
store = persist(store, {
  name: 'app-store',
  getStorage: () => sessionStorage,
  partialize: (state) => ({
    ...state,
    selectedMyTodoTransactionRowId: undefined,
    offset: undefined,
    nonModalWindowState: undefined,
    setNonModalProps: undefined,
    modalProps: undefined,
    archivedVault: undefined,
    modalWindowType: undefined,
    editedEntity: undefined,
    cellTransactionsLoaded: undefined,
    dockTransactionListView: undefined,
    highlightedPeriodOffset: undefined,
    // A timestamp restored from sessionStorage would claim the data was
    // fresh as of a page load that has since been thrown away.
    dashboardRefreshedAt: undefined,
    // Must not survive a reload, or the dashboard's load precedence would
    // never be applied again for the rest of the browser session.
    dashboardBootstrappedTeamId: undefined,
  }),
})

const useAppStore = create(store)

export default useAppStore
